import React from 'react';
import { FileText, Handshake, Monitor, Users, Shield, CreditCard, Lock, AlertTriangle, Settings, XCircle, Scale, Edit, Mail, MapPin } from 'lucide-react';

interface TermsSection {
  id: string;
  title: string;
  icon: React.ReactNode;
  content: React.ReactNode;
}

const TermsOfService: React.FC = () => {
  const sections: TermsSection[] = [
    {
      id: 'acceptance',
      title: '1. Acceptance of Terms',
      icon: <Handshake className="w-6 h-6" />,
      content: (
        <p className="text-gray-700 leading-relaxed">
          By accessing or using the website and services provided by <span className="text-blue-600 font-semibold">Techno-Stack-Labs</span>, you agree to be bound by these Terms of Service and all applicable laws. If you do not agree, please do not use our services.
        </p>
      )
    },
    {
      id: 'services-offered',
      title: '2. Services Offered',
      icon: <Monitor className="w-6 h-6" />,
      content: (
        <p className="text-gray-700 leading-relaxed">
          We offer technology consulting, web and mobile development, UI/UX design, and data analytics services. The scope and pricing of each service will be agreed upon via written proposal or contract before work begins.
        </p>
      )
    },
    {
      id: 'website-use',
      title: '3. Use of Website',
      icon: <Shield className="w-6 h-6" />,
      content: (
        <p className="text-gray-700 leading-relaxed">
          You agree to use our website for lawful purposes only. You must not attempt to gain unauthorized access to our systems or interfere with the operation of our website or services.
        </p>
      )
    },
    {
      id: 'client-responsibilities',
      title: '4. Client Responsibilities',
      icon: <Users className="w-6 h-6" />,
      content: (
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
          <p className="text-gray-700 leading-relaxed">
            Clients must provide accurate information, timely feedback, and all necessary access or assets for project execution. <span className="text-amber-600 font-semibold">Delays in client inputs may affect project timelines.</span>
          </p>
        </div>
      )
    },
    {
      id: 'intellectual-property',
      title: '5. Intellectual Property',
      icon: <Lock className="w-6 h-6" />,
      content: (
        <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
          <p className="text-gray-700 leading-relaxed">
            Unless otherwise agreed, all code, designs, and deliverables remain the intellectual property of <span className="text-blue-600 font-semibold">Techno-Stack-Labs</span> until full payment is received. Post-payment, ownership terms are governed by the project agreement.
          </p>
        </div>
      )
    },
    {
      id: 'payments-refunds',
      title: '6. Payments & Refunds',
      icon: <CreditCard className="w-6 h-6" />,
      content: (
        <p className="text-gray-700 leading-relaxed">
          Payment terms will be defined in project agreements or invoices. <span className="text-red-600 font-semibold">Once services have commenced or been delivered, payments are non-refundable</span> unless explicitly stated otherwise.
        </p>
      )
    },
    {
      id: 'confidentiality',
      title: '7. Confidentiality',
      icon: <Shield className="w-6 h-6" />,
      content: (
        <p className="text-gray-700 leading-relaxed">
          We maintain confidentiality of your business information and expect clients to do the same regarding our proposals, pricing, and processes.
        </p>
      )
    },
    {
      id: 'liability-limitation',
      title: '8. Limitation of Liability',
      icon: <AlertTriangle className="w-6 h-6" />,
      content: (
        <div className="bg-red-50 p-4 rounded-lg border border-red-200">
          <p className="text-gray-700 leading-relaxed">
            We are not liable for indirect, incidental, or consequential damages arising from the use of our website or services. <span className="text-red-600 font-semibold">Our total liability in any matter shall not exceed the fees paid for the specific service.</span>
          </p>
        </div>
      )
    },
    {
      id: 'third-party-tools',
      title: '9. Third-Party Tools & Integrations',
      icon: <Settings className="w-6 h-6" />,
      content: (
        <p className="text-gray-700 leading-relaxed">
          We may use third-party tools, platforms, or APIs to deliver services. We are not liable for issues caused by third-party services or outages beyond our control.
        </p>
      )
    },
    {
      id: 'termination',
      title: '10. Termination',
      icon: <XCircle className="w-6 h-6" />,
      content: (
        <p className="text-gray-700 leading-relaxed">
          Either party may terminate the engagement with notice, based on the terms of the project agreement. <span className="text-blue-600 font-semibold">Upon termination, all outstanding dues must be settled immediately.</span>
        </p>
      )
    },
    {
      id: 'governing-law',
      title: '11. Governing Law',
      icon: <Scale className="w-6 h-6" />,
      content: (
        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
          <p className="text-gray-700 leading-relaxed">
            These terms are governed by and construed in accordance with the laws of India. All disputes shall be subject to the jurisdiction of the courts of <span className="text-blue-600 font-semibold">[Insert City]</span>, India.
          </p>
        </div>
      )
    },
    {
      id: 'changes-to-terms',
      title: '12. Changes to Terms',
      icon: <Edit className="w-6 h-6" />,
      content: (
        <p className="text-gray-700 leading-relaxed">
          We reserve the right to modify these Terms at any time. Updates will be posted on this page with a revised effective date. <span className="text-blue-600 font-semibold">Continued use of our services constitutes acceptance of the changes.</span>
        </p>
      )
    }
  ];

  return (
    <div>
        <section className="py-16 md:py-24 bg-gradient-to-r from-blue-900 to-indigo-600">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full mb-6 shadow-lg">
            <FileText className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r text-white">
            Terms of Service
          </h1>
        </div>
        </section>
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-500/5 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-purple-500/5 rounded-full blur-3xl animate-pulse delay-1000"></div>
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-6 py-12">
        

        {/* Terms Sections */}
        <div className="space-y-8">
          {sections.map((section, index) => (
            <div
              key={section.id}
              className="bg-white/80 backdrop-blur-sm border border-gray-200 rounded-xl p-8 hover:bg-white/90 transition-all duration-300 hover:shadow-xl hover:shadow-blue-500/10"
              style={{
                animationDelay: `${index * 100}ms`,
                animation: 'fadeInUp 0.6s ease-out forwards'
              }}
            >
              <div className="flex items-center space-x-4 mb-6">
                <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-r from-blue-100 to-purple-100 rounded-lg border border-blue-200">
                  <div className="text-blue-600">
                    {section.icon}
                  </div>
                </div>
                <h2 className="text-2xl font-bold text-gray-800">{section.title}</h2>
              </div>
              <div className="ml-16">
                {section.content}
              </div>
            </div>
          ))}

          {/* Contact Section */}
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 backdrop-blur-sm border border-blue-200 rounded-xl p-8 mt-12 shadow-lg">
            <div className="flex items-center space-x-4 mb-6">
              <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-r from-blue-100 to-purple-100 rounded-lg border border-blue-200">
                <Mail className="w-6 h-6 text-blue-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800">13. Contact Us</h2>
            </div>
            <div className="ml-16 space-y-4">
              <p className="text-gray-700 leading-relaxed mb-6">
                If you have any questions about these Terms, please contact us at:
              </p>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="flex items-center space-x-3 bg-white/80 p-4 rounded-lg border border-gray-200 shadow-sm">
                  <Mail className="w-5 h-5 text-blue-600 flex-shrink-0" />
                  <div>
                    <p className="text-gray-500 text-sm">Email</p>
                    <p className="text-blue-600 font-semibold">info@technostacklabs.com</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3 bg-white/80 p-4 rounded-lg border border-gray-200 shadow-sm">
                  <MapPin className="w-5 h-5 text-blue-600 flex-shrink-0" />
                  <div>
                    <p className="text-gray-500 text-sm">Address</p>
                    <p className="text-gray-700">D-107, Vyapar Marg, D Block, Sector 2, Noida, Uttar Pradesh 201301</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
    
      </div>

      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
    </div>
  );
};

export default TermsOfService;